import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { CompositeChart } from "../composite-chart";
import {
  CompositionMathTrigger,
} from "../composition-math-tooltip";
import type { ExecutionState } from "../../hooks/use-composite-execution";
import type {
  PayoffPoint,
  PerpLeg,
  PredictionLeg,
} from "../../lib/composePayoff";
import type { Scenario } from "../../lib/scenarios";

const PERP_COLOR = "#a78bfa";
const PRED_COLOR = "#60a5fa";
const BRAND = "#9ecbff";
const GOLD = "#f5a623";

const mono10: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  fontWeight: 600,
};

// ============================================================================
// DesktopCompositeView — the trading workspace shown after the user clicks
// "Reveal Composite". The two inner mini-charts have been removed; the
// composite payoff curve takes center stage and is flanked by a strategy
// banner, a metrics hero, and a scenarios grid.
// ============================================================================

export function DesktopCompositeView({
  payoff,
  derived,
  perpLeg,
  predLeg,
  perpCoin,
  perpEntry,
  predQuestion,
  predOutcome,
  activeScenario,
  setHoverScenario,
  setPinnedScenario,
  mathOpen,
  setMathOpen,
  createOpen,
  setCreateOpen,
  executionState,
}: {
  payoff: ReadonlyArray<PayoffPoint>;
  derived: {
    mark: number;
    atMark: number;
    maxGain: number;
    maxLoss: number;
    breakevens: number[];
    scenarios: Scenario[];
    strategy: string;
  };
  perpLeg: PerpLeg;
  predLeg: PredictionLeg;
  perpCoin: string;
  perpEntry: number;
  predQuestion: string;
  predOutcome: "yes" | "no";
  activeScenario: Scenario | null;
  setHoverScenario: (s: Scenario | null) => void;
  setPinnedScenario: Dispatch<SetStateAction<Scenario | null>>;
  mathOpen: boolean;
  setMathOpen: Dispatch<SetStateAction<boolean>>;
  createOpen: boolean;
  setCreateOpen: Dispatch<SetStateAction<boolean>>;
  executionState: ExecutionState;
}) {
  const rr =
    derived.maxLoss !== 0
      ? (derived.maxGain / Math.abs(derived.maxLoss)).toFixed(2)
      : "—";
  const beText =
    derived.breakevens.length === 0
      ? "—"
      : derived.breakevens.length === 1
        ? formatPrice(derived.breakevens[0]!)
        : derived.breakevens.map(formatPrice).join(" / ");

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        background: `
          radial-gradient(ellipse 50% 30% at 50% 0%, ${BRAND}06 0%, transparent 70%),
          radial-gradient(ellipse 80% 40% at 50% 100%, rgba(0,0,0,0.32) 0%, transparent 70%),
          var(--bg-0)
        `,
      }}
    >
      {/* ── Strategy banner — full width strip below the breadcrumb ── */}
      <StrategyBanner
        strategy={derived.strategy}
        perpSide={perpLeg.side}
        perpCoin={perpCoin}
        perpSize={perpLeg.size}
        predOutcome={predOutcome}
        predShares={predLeg.shares}
        predQuestion={predQuestion}
        mathOpen={mathOpen}
        onMathToggle={() => setMathOpen((v) => !v)}
        createOpen={createOpen}
        onCreateToggle={() => setCreateOpen((v) => !v)}
        derivedReady={!!derived}
        executionState={executionState}
      />

      {/* ── Metrics hero — six big stats above the chart ── */}
      <MetricsHero
        atMark={derived.atMark}
        maxGain={derived.maxGain}
        maxLoss={derived.maxLoss}
        rr={rr}
        beText={beText}
        notional={
          perpLeg.size * perpEntry +
          (predLeg.shares * predLeg.entryCents) / 100
        }
      />

      {/* ── Hero composite chart — takes most of the vertical space ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: "8px 14px 0",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "relative",
            height: "100%",
            borderRadius: 6,
            border: "1px solid var(--line-2)",
            background: `
              radial-gradient(ellipse 30% 40% at 50% 0%, rgba(255,255,255,0.025) 0%, transparent 70%),
              linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%)
            `,
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.04), 0 1px 0 rgba(0,0,0,0.5)",
            overflow: "hidden",
          }}
        >
          {/* Chart label corner — small terminal touch */}
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 14,
              ...mono10,
              fontSize: 9,
              color: "var(--fg-dim)",
              letterSpacing: "0.22em",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            COMPOSITE PAYOFF · {perpCoin}USD
          </div>
          <CompositeChart
            points={payoff}
            mark={derived.mark}
            threshold={predLeg.threshold}
            liquidation={perpLeg.liquidation}
            highlight={
              activeScenario
                ? { price: activeScenario.price, label: activeScenario.label }
                : null
            }
          />
        </div>
      </div>

      {/* ── Scenarios row — horizontal cards across the bottom ── */}
      <ScenarioStrip
        scenarios={derived.scenarios}
        activePrice={activeScenario?.price ?? null}
        onHover={setHoverScenario}
        onSelect={(s) =>
          setPinnedScenario((prev) =>
            prev && prev.price === s.price ? null : s,
          )
        }
      />
    </div>
  );
}

// ============================================================================
// Strategy banner
// ============================================================================

function StrategyBanner({
  strategy,
  perpSide,
  perpCoin,
  perpSize,
  predOutcome,
  predShares,
  predQuestion,
  mathOpen,
  onMathToggle,
  createOpen,
  onCreateToggle,
  derivedReady,
  executionState,
}: {
  strategy: string;
  perpSide: "long" | "short";
  perpCoin: string;
  perpSize: number;
  predOutcome: "yes" | "no";
  predShares: number;
  predQuestion: string;
  mathOpen: boolean;
  onMathToggle: () => void;
  createOpen: boolean;
  onCreateToggle: () => void;
  derivedReady: boolean;
  executionState: ExecutionState;
}) {
  const sideColor = perpSide === "long" ? "var(--up)" : "var(--dn)";
  const outcomeColor = predOutcome === "yes" ? "var(--up)" : "var(--dn)";
  const ctaActive =
    derivedReady &&
    executionState.stage !== "success" &&
    executionState.stage === "idle";
  const ctaLabel = createOpen
    ? "REVIEWING…"
    : executionState.stage === "success"
      ? "FILLED ✓"
      : "CREATE COMPOSITE";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 18px",
        borderBottom: "1px solid var(--line-2)",
        background:
          "linear-gradient(180deg, rgba(245,166,35,0.04) 0%, transparent 100%)",
      }}
    >
      <CompositionMathTrigger open={mathOpen} onToggle={onMathToggle} />
      <span
        style={{
          ...mono10,
          fontSize: 9,
          color: GOLD,
          letterSpacing: "0.26em",
          fontWeight: 700,
        }}
      >
        STRATEGY
      </span>
      <span
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 18,
          fontWeight: 600,
          color: "var(--fg-max)",
          letterSpacing: "-0.005em",
        }}
      >
        {strategy}
      </span>
      <Sep />
      <LegInline color={PERP_COLOR} venue="PERP">
        <span style={{ color: sideColor, fontWeight: 700 }}>
          {perpSide.toUpperCase()}
        </span>{" "}
        <span style={{ color: "var(--fg-hi)" }}>
          {perpSize} {perpCoin}
        </span>
      </LegInline>
      <span style={{ color: "var(--fg-dim)", fontSize: 14 }}>×</span>
      <LegInline color={PRED_COLOR} venue="PRED">
        <span style={{ color: outcomeColor, fontWeight: 700 }}>
          {predOutcome.toUpperCase()}
        </span>{" "}
        <span style={{ color: "var(--fg-hi)" }}>{predShares}</span>{" "}
        <span
          style={{
            color: "var(--fg-meta)",
            maxWidth: 280,
            display: "inline-block",
            verticalAlign: "bottom",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={predQuestion}
        >
          {predQuestion}
        </span>
      </LegInline>
      <button
        type="button"
        onClick={onCreateToggle}
        disabled={!derivedReady}
        style={{
          marginLeft: "auto",
          ...mono10,
          fontSize: 10,
          color: ctaActive ? "var(--bg-0)" : "var(--fg-hi)",
          background: createOpen
            ? "var(--fg-max)"
            : ctaActive
              ? `linear-gradient(180deg, ${BRAND} 0%, #7ab3ee 100%)`
              : "transparent",
          border: createOpen
            ? "1px solid var(--fg-max)"
            : ctaActive
              ? `1px solid ${BRAND}`
              : "1px solid var(--line-3)",
          padding: "7px 14px",
          borderRadius: 2,
          cursor: derivedReady ? "pointer" : "not-allowed",
          letterSpacing: "0.18em",
          fontWeight: 700,
          boxShadow: ctaActive
            ? `0 0 18px ${BRAND}33, inset 0 1px 0 rgba(255,255,255,0.3)`
            : "none",
          whiteSpace: "nowrap",
        }}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

function Sep() {
  return (
    <span
      aria-hidden
      style={{
        width: 1,
        height: 20,
        background: "var(--line-2)",
        flexShrink: 0,
      }}
    />
  );
}

function LegInline({
  venue,
  color,
  children,
}: {
  venue: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontFamily: "var(--f-mono)",
        fontSize: 11,
        color: "var(--fg-meta)",
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...mono10,
          fontSize: 9,
          color,
          padding: "2px 6px",
          borderRadius: 2,
          border: `1px solid ${color}55`,
          background: `${color}10`,
        }}
      >
        {venue}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {children}
      </span>
    </span>
  );
}

// ============================================================================
// Metrics hero — six big numbers, plus an animated entry cascade
// ============================================================================

function MetricsHero({
  atMark,
  maxGain,
  maxLoss,
  rr,
  beText,
  notional,
}: {
  atMark: number;
  maxGain: number;
  maxLoss: number;
  rr: string;
  beText: string;
  notional: number;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        padding: "12px 14px 6px",
        gap: 10,
      }}
    >
      <BigMetric
        idx={0}
        label="AT MARK"
        value={signedUsd(atMark)}
        color={atMark >= 0 ? "var(--up)" : "var(--dn)"}
        accent
      />
      <BigMetric
        idx={1}
        label="MAX GAIN"
        value={signedUsd(maxGain)}
        color="var(--up)"
      />
      <BigMetric
        idx={2}
        label="MAX LOSS"
        value={signedUsd(maxLoss)}
        color="var(--dn)"
      />
      <BigMetric idx={3} label="R : R" value={rr} color="var(--fg-max)" />
      <BigMetric
        idx={4}
        label="BREAKEVEN"
        value={beText}
        color="var(--fg-max)"
      />
    </div>
  );
}

function BigMetric({
  label,
  value,
  color,
  accent,
  idx,
}: {
  label: string;
  value: string;
  color: string;
  accent?: boolean;
  idx: number;
}) {
  return (
    <div
      style={{
        position: "relative",
        padding: "10px 12px 11px",
        borderRadius: 4,
        border: accent
          ? `1px solid ${BRAND}33`
          : "1px solid var(--line-2)",
        background: accent
          ? `linear-gradient(180deg, ${BRAND}10 0%, transparent 100%), var(--bg-1)`
          : "var(--bg-1)",
        boxShadow: accent
          ? `inset 2px 0 0 ${BRAND}88, 0 0 18px ${BRAND}10`
          : "none",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 0,
        animation: `hh-stagger-in 460ms cubic-bezier(0.16, 1, 0.3, 1) ${idx * 60}ms backwards`,
      }}
    >
      <span
        style={{
          ...mono10,
          fontSize: 8,
          color: "var(--fg-dim)",
          letterSpacing: "0.22em",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 18,
          color,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ============================================================================
// Scenarios — horizontal cards w/ magnitude bar
// ============================================================================

function ScenarioStrip({
  scenarios,
  activePrice,
  onHover,
  onSelect,
}: {
  scenarios: ReadonlyArray<Scenario>;
  activePrice: number | null;
  onHover: (s: Scenario | null) => void;
  onSelect: (s: Scenario) => void;
}) {
  const maxAbs = scenarios.reduce(
    (m, s) => Math.max(m, Math.abs(s.totalPnl)),
    1,
  );
  return (
    <div
      onMouseLeave={() => onHover(null)}
      style={{
        display: "grid",
        gridAutoFlow: "column",
        gridAutoColumns: "minmax(108px, 1fr)",
        gap: 6,
        padding: "10px 14px 14px",
        overflowX: "auto",
      }}
    >
      {scenarios.map((s, i) => (
        <ScenarioCard
          key={`${s.label}-${s.price}`}
          scenario={s}
          maxAbs={maxAbs}
          active={
            activePrice !== null && Math.abs(activePrice - s.price) < 1e-6
          }
          onMouseEnter={() => onHover(s)}
          onClick={() => onSelect(s)}
          idx={i}
        />
      ))}
    </div>
  );
}

function ScenarioCard({
  scenario,
  maxAbs,
  active,
  onMouseEnter,
  onClick,
  idx,
}: {
  scenario: Scenario;
  maxAbs: number;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  idx: number;
}) {
  const pnlColor =
    scenario.totalPnl > 0
      ? "var(--up)"
      : scenario.totalPnl < 0
        ? "var(--dn)"
        : "var(--fg-meta)";
  const labelColor =
    scenario.tone === "liq"
      ? "var(--sev-crit)"
      : scenario.tone === "threshold"
        ? PRED_COLOR
        : scenario.tone === "mark"
          ? BRAND
          : "var(--fg-meta)";
  const magnitude = Math.min(1, Math.abs(scenario.totalPnl) / maxAbs);
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "8px 10px",
        textAlign: "left",
        borderRadius: 3,
        border: active
          ? `1px solid ${BRAND}66`
          : "1px solid var(--line-2)",
        background: active
          ? `linear-gradient(180deg, ${BRAND}14 0%, transparent 100%), var(--bg-1)`
          : "var(--bg-1)",
        boxShadow: active ? `inset 2px 0 0 ${BRAND}, 0 0 16px ${BRAND}14` : "none",
        cursor: "pointer",
        transition:
          "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
        animation: `hh-stagger-in 460ms cubic-bezier(0.16, 1, 0.3, 1) ${100 + idx * 40}ms backwards`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 6,
        }}
      >
        <span
          style={{
            ...mono10,
            fontSize: 9,
            color: labelColor,
            letterSpacing: "0.16em",
            fontWeight: 600,
          }}
        >
          {scenario.label}
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 9,
            color: "var(--fg-dim)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatPrice(scenario.price)}
        </span>
      </div>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 14,
          color: pnlColor,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
        }}
      >
        {signedUsd(scenario.totalPnl)}
      </span>
      {/* Magnitude bar */}
      <div
        aria-hidden
        style={{
          height: 2,
          background: "rgba(255,255,255,0.05)",
          borderRadius: 1,
          overflow: "hidden",
          marginTop: 2,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${magnitude * 100}%`,
            background: pnlColor,
            opacity: 0.85,
            boxShadow: `0 0 6px ${pnlColor}55`,
          }}
        />
      </div>
    </button>
  );
}

// ============================================================================
// Format helpers
// ============================================================================

function signedUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(n) < 10 ? 2 : 0,
  });
  return neg ? `−${abs}` : `+${abs}`;
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 10_000) return `$${(n / 1000).toFixed(2)}K`;
  if (n >= 1000) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toPrecision(3)}`;
}
